"use no memo";
import { useQuery } from "@tanstack/react-query";
import {
	Archive,
	Calendar,
	CheckCircle,
	Edit05,
	Expand03,
	MessageAlertSquare,
	Plus,
	Trash02,
	User01,
} from "@untitledui/icons";
import type { ReactNode } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { Toast } from "@/components/Molecules/Toast/Toast";
import { WorkflowRuleConditionLabel } from "@/components/Molecules/WorkflowRuleConditionLabel/WorkflowRuleConditionLabel";
import { WorkflowRuleOverview } from "@/components/Molecules/WorkflowRuleOverview/WorkflowRuleOverview";
import { DeleteWorkflowRuleModal } from "@/components/Organisms/DeleteWorkflowRuleModal/DeleteWorkflowRuleModal";
import { ConditionDateValueInput } from "@/components/Organisms/shared/ConditionDateValueInput";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { assetQueryOptions } from "@/services/asset/asset.options";
import { authQueryOptions } from "@/services/auth/auth.options";
import { formDefinitionQueryOptions } from "@/services/form-definition";
import { jobQueryOptions } from "@/services/job/job.options";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import { organizationQueryOptions } from "@/services/organization";
import { recordQueryOptions } from "@/services/record/record.options";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";
import {
	useActivateWorkflowRuleVersion,
	useArchiveWorkflowRule,
	useEditWorkflowRule,
	workflowQueryOptions,
} from "@/services/workflow";
import type { WorkflowRuleDisplay } from "@/services/workflow/workflow.types";
import { getEntityAuditDisplayFields } from "@/utils/entity-audit-display";
import {
	buildFieldLabelMap,
	formatActionDisplay,
	formatRuleOverviewParts,
	formatTriggerDetail,
	formatOperatorLabel,
	formatTriggerLabel,
	getConditionDisplayParts,
} from "@/utils/workflow-rules/format";
import type {
	ActionParams,
	ActionRow,
	ConditionRow,
	RuleFormDraft,
	TriggerType,
} from "@/utils/workflow-rules/workflow-rule-form-utils";
import {
	buildRuleVersionPayload,
	ENTITY_TYPE_TO_KIND,
	hydrateRuleFormDraft,
	makeEmptyActionRow,
	makeEmptyConditionRow,
	makeEmptyRuleFormDraft,
	makeParamsForActionType,
	operatorsForDataType,
	RULE_FORM_CONDITION_FIELDS,
	RULE_FORM_NO_VALUE_OPERATORS,
	validateRuleFormDraft,
} from "@/utils/workflow-rules/workflow-rule-form-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

type DrawerTab = "details" | "activity";

export interface WorkflowRuleDetailDrawerProps {
	/** Rule id to fetch/display; pass null to keep drawer closed */
	ruleId: string | null;
	/** Organization-id header value */
	organizationId: string;
	/** Lifecycle id for resolving status names (null for record types) */
	lifecycleId?: string | null;
	/** "platform" for admin, "organization" for org-admin (affects assignee picker) */
	mode?: "platform" | "organization";
	/** Whether the current user has EditWorkflowRule permission. Defaults to true. */
	canEdit?: boolean;
	/** The specific type ID (jobTypeId, ticketTypeId, etc.) used to load custom form fields. */
	entityTypeId?: string;
	/** Called when the drawer requests to close */
	onClose: () => void;
	/** Called when the user clicks "Full View" — navigates to the rule detail page */
	onFullView?: (ruleId: string) => void;
}

// ─── Inline helpers ───────────────────────────────────────────────────────────

function StatusBadge({
	status,
	t,
}: {
	status: string;
	t: ReturnType<typeof useAppTranslation>["t"];
}) {
	if (status === "active") {
		return (
			<Badge color="success" size="sm">
				{t("admin.workflowRules.table.filters.status.active")}
			</Badge>
		);
	}
	if (status === "archived") {
		return (
			<Badge color="gray" size="sm" className="opacity-60">
				{t("admin.workflowRules.table.filters.status.archived")}
			</Badge>
		);
	}
	return (
		<Badge color="gray" size="sm">
			{t("admin.workflowRules.table.filters.status.draft")}
		</Badge>
	);
}

function InlineError({ message }: { message?: string }) {
	if (!message) return null;
	return <p className="mt-1 text-xs text-error-600">{message}</p>;
}

function SectionHeader({
	title,
	description,
}: {
	title: string;
	description: string;
}) {
	return (
		<div>
			<p className="text-sm font-semibold text-primary">{title}</p>
			<p className="text-xs text-tertiary">{description}</p>
		</div>
	);
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
	title: string;
	description: string;
	confirmLabel: string;
	confirmColor?: "primary" | "primary-destructive";
	isPending: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

function ConfirmDialog({
	title,
	description,
	confirmLabel,
	confirmColor = "primary",
	isPending,
	onConfirm,
	onCancel,
}: ConfirmDialogProps) {
	const { t } = useAppTranslation();
	return (
		<div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40">
			<div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
				<h2 className="text-lg font-semibold text-gray-900">{title}</h2>
				<p className="mt-2 text-sm text-gray-600">{description}</p>
				<div className="mt-6 flex justify-end gap-3">
					<Button
						color="secondary"
						size="sm"
						onClick={onCancel}
						isDisabled={isPending}
					>
						{t("common.actions.cancel")}
					</Button>
					<Button
						color={confirmColor}
						size="sm"
						onClick={onConfirm}
						isDisabled={isPending}
						isLoading={isPending}
					>
						{confirmLabel}
					</Button>
				</div>
			</div>
		</div>
	);
}

// ─── Details read-only tab ────────────────────────────────────────────────────

interface DetailsTabProps {
	rule: WorkflowRuleDisplay;
	statusNameMap: Record<string, string>;
	userNameMap: Record<string, string>;
	subOrgNameMap: Record<string, string>;
	entityKind: string;
	entityTypeId?: string;
	organizationId: string;
	canEdit: boolean;
	onEditClick: () => void;
	t: ReturnType<typeof useAppTranslation>["t"];
}

function DetailsTab({
	rule,
	statusNameMap,
	userNameMap,
	subOrgNameMap,
	entityKind,
	entityTypeId,
	organizationId,
	canEdit,
	onEditClick,
	t,
}: DetailsTabProps) {
	const version = rule.latestVersion ?? rule.activeVersion;
	const isDraft = rule.status === "draft";
	const isActive = rule.status === "active";
	const isArchived = rule.status === "archived";

	const formDefParams = useMemo(() => {
		if (!entityTypeId) return undefined;
		if (entityKind === "JobType") return { JobTypeId: entityTypeId };
		if (entityKind === "TicketType") return { TicketTypeId: entityTypeId };
		if (entityKind === "AssetType") return { AssetTypeId: entityTypeId };
		if (entityKind === "RecordType") return { RecordTypeId: entityTypeId };
		return undefined;
	}, [entityKind, entityTypeId]);

	const formDefQuery = useQuery({
		...formDefinitionQueryOptions.current(formDefParams, { organizationId }),
		enabled: !!formDefParams,
	});

	const fieldLabelMap = useMemo(
		() =>
			buildFieldLabelMap(
				(formDefQuery.data?.fieldDefinitions ?? [])
					.filter((f): f is typeof f & { label: string } => !!f.label)
					.map((f) => ({ id: f.id, label: f.label })),
			),
		[formDefQuery.data],
	);

	const overviewParts = useMemo(
		() =>
			formatRuleOverviewParts(
				rule,
				statusNameMap,
				t,
				fieldLabelMap,
				userNameMap,
			),
		[rule, statusNameMap, userNameMap, fieldLabelMap, t],
	);

	const conditions = version?.condition?.conditions ?? [];
	const actions = version?.actions ?? [];

	return (
		<div className="space-y-6 px-5 pb-5">
			{(isActive || isArchived) && (
				<div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
					<MessageAlertSquare className="mt-0.5 size-4 shrink-0 text-blue-500" />
					<p className="text-sm text-blue-800">
						{isActive
							? t("admin.workflowRules.drawer.banner.active")
							: t("admin.workflowRules.drawer.banner.archived")}
					</p>
				</div>
			)}

			{/* Rule Overview */}
			<div className="space-y-2">
				<h3 className="text-[16px] font-semibold text-gray-900">
					{t("admin.workflowRules.drawer.sections.ruleOverview")}
				</h3>
				<div className="rounded-lg bg-gray-50 p-4">
					{overviewParts ? (
						<WorkflowRuleOverview parts={overviewParts} t={t} />
					) : (
						<p className="font-mono text-sm text-gray-400">—</p>
					)}
				</div>
			</div>

			{/* Rule Details */}
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<h3 className="text-[16px] font-semibold text-gray-900">
						{t("admin.workflowRules.drawer.sections.ruleDetails")}
					</h3>
					{isDraft && canEdit ? (
						<Button
							color="secondary"
							size="sm"
							iconLeading={Edit05}
							aria-label={t("admin.workflowRules.drawer.editTooltip")}
							onClick={onEditClick}
						/>
					) : null}
				</div>

				<div className="rounded-lg border border-gray-200 bg-white p-4">
					<div className="grid grid-cols-2 gap-x-8 gap-y-4">
						<div>
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{t("admin.workflowRules.drawer.fields.name")}
							</p>
							<p className="mt-1 text-sm font-semibold text-gray-900">
								{rule.name ?? <EmptyValue />}
							</p>
						</div>
						<div>
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{t("admin.workflowRules.drawer.fields.status")}
							</p>
							<div className="mt-1">
								<StatusBadge status={rule.status} t={t} />
							</div>
						</div>
						{rule.description ? (
							<div className="col-span-2">
								<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
									{t("admin.workflowRules.drawer.fields.description")}
								</p>
								<p className="mt-1 text-sm font-semibold text-gray-900">
									{rule.description}
								</p>
							</div>
						) : null}
						<div>
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{t("admin.workflowRules.drawer.fields.version")}
							</p>
							<p className="mt-1 text-sm font-semibold text-gray-900">
								{version?.version ?? <EmptyValue />}
							</p>
						</div>
						<div>
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{t("admin.workflowRules.drawer.fields.scope")}
							</p>
							<p className="mt-1 text-sm font-semibold text-gray-900">
								{(() => {
									const scopeIds = (rule.scopes ?? [])
										.map((s) => s.subOrganizationId)
										.filter((id): id is string => !!id);
									if (scopeIds.length === 0) {
										return t("admin.workflowRules.drawer.scope.organization");
									}
									return scopeIds
										.map((id) => subOrgNameMap[id] ?? id)
										.join(", ");
								})()}
							</p>
						</div>
						<div className="col-span-2">
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{t("admin.workflowRules.drawer.fields.id")}
							</p>
							<div className="mt-1">
								<CopyableId value={rule.id} detailText />
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Trigger */}
			<div className="space-y-2">
				<h3 className="text-[16px] font-semibold text-gray-900">
					{t("admin.workflowRules.drawer.sections.trigger")}
				</h3>
				<div className="rounded-lg border border-gray-200 bg-white p-4">
					{version?.trigger ? (
						<div className="space-y-0.5">
							<p className="text-sm font-semibold text-gray-900">
								{formatTriggerLabel(version.trigger.type, t)}
							</p>
							{(() => {
								const detail = formatTriggerDetail(
									version.trigger,
									statusNameMap,
								);
								return detail ? (
									<p className="text-sm text-gray-500">{detail}</p>
								) : null;
							})()}
						</div>
					) : (
						<EmptyValue />
					)}
				</div>
			</div>

			{/* Conditions */}
			<div className="space-y-2">
				<h3 className="text-[16px] font-semibold text-gray-900">
					{t("admin.workflowRules.drawer.sections.conditions")}
				</h3>
				<div className="rounded-lg border border-gray-200 bg-white p-4">
					{conditions.length > 0 ? (
						<ul className="space-y-1">
							{conditions.map((cond, i) => (
								<li
									key={cond.entityPropertyName ?? cond.columnDefinitionId ?? i}
								>
									<WorkflowRuleConditionLabel
										parts={getConditionDisplayParts(cond, fieldLabelMap, t)}
									/>
								</li>
							))}
						</ul>
					) : (
						<p className="text-sm italic text-gray-400">
							{t("admin.workflowRules.drawer.conditions.empty")}
						</p>
					)}
				</div>
			</div>

			{/* Actions */}
			<div className="space-y-2">
				<h3 className="text-[16px] font-semibold text-gray-900">
					{t("admin.workflowRules.drawer.sections.actions")}
				</h3>
				<div className="space-y-2">
					{actions.length > 0 ? (
						actions.map((action, i) => {
							const { label, detail } = formatActionDisplay(
								action,
								statusNameMap,
								t,
								userNameMap,
							);
							return (
								<div
									key={action.type ? `${action.type}-${i}` : i}
									className="rounded-lg border border-gray-200 bg-white p-4"
								>
									<p className="text-sm font-semibold text-gray-900">{label}</p>
									{detail ? (
										<p className="mt-0.5 text-sm text-gray-500">{detail}</p>
									) : null}
								</div>
							);
						})
					) : (
						<div className="rounded-lg border border-gray-200 bg-white p-4">
							<EmptyValue />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

// ─── Edit details tab ─────────────────────────────────────────────────────────

interface EditDetailsTabProps {
	draft: RuleFormDraft;
	errors: Record<string, string>;
	entityKind: string;
	entityTypeId?: string;
	mode: "platform" | "organization";
	organizationId: string;
	lifecycleStatuses: {
		id: string;
		name?: string | null;
		displayOrder: number;
	}[];
	onDraftChange: (patch: Partial<RuleFormDraft>) => void;
	onErrorsClear: (keys: string[]) => void;
	t: ReturnType<typeof useAppTranslation>["t"];
}

function EditDetailsTab({
	draft,
	errors,
	entityKind,
	entityTypeId,
	mode,
	organizationId,
	lifecycleStatuses,
	onDraftChange,
	onErrorsClear,
	t,
}: EditDetailsTabProps) {
	// Entity type queries for "Create Entity" action
	const createEntityClasses = new Set(
		draft.actions
			.filter((a) => a.type === "create_entity")
			.map(
				(a) =>
					(a.params as Extract<ActionParams, { type: "create_entity" }>)
						.entityClass,
			),
	);

	const recordTypesQuery = useQuery({
		...recordQueryOptions.typesAll({ OrganizationId: organizationId }),
		enabled: createEntityClasses.has("record"),
	});
	const jobTypesQuery = useQuery({
		...jobQueryOptions.typesAll({ organizationId }),
		enabled: createEntityClasses.has("job"),
	});
	const ticketTypesQuery = useQuery({
		...ticketQueryOptions.typesAll({ organizationId }),
		enabled: createEntityClasses.has("ticket"),
	});
	const ticketPrioritiesQuery = useQuery({
		...ticketQueryOptions.priorities({ organizationId }),
		enabled: createEntityClasses.has("ticket"),
	});
	const ticketPriorities = ticketPrioritiesQuery.data ?? [];

	// Form definition fields — appended to the static condition field list
	const formDefParams = useMemo(() => {
		if (!entityTypeId) return undefined;
		if (entityKind === "JobType") return { JobTypeId: entityTypeId };
		if (entityKind === "TicketType") return { TicketTypeId: entityTypeId };
		if (entityKind === "AssetType") return { AssetTypeId: entityTypeId };
		if (entityKind === "RecordType") return { RecordTypeId: entityTypeId };
		return undefined;
	}, [entityKind, entityTypeId]);

	const formDefQuery = useQuery({
		...formDefinitionQueryOptions.current(formDefParams, { organizationId }),
		enabled: !!formDefParams,
	});

	const customConditionFields = useMemo(
		() =>
			(formDefQuery.data?.fieldDefinitions ?? [])
				.filter((f): f is typeof f & { label: string } => !!f.label)
				.map((f) => ({
					id: f.id,
					label: f.label,
					dataType: f.dataType,
					validValues:
						Array.isArray(f.validationRules?.validValues) &&
						f.validationRules.validValues.length > 0
							? (f.validationRules.validValues as string[])
							: undefined,
				})),
		[formDefQuery.data],
	);

	// Stable refs so the effect below only depends on customConditionFields.
	const conditionsRef = useRef(draft.conditions);
	conditionsRef.current = draft.conditions;
	const onDraftChangeRef = useRef(onDraftChange);
	onDraftChangeRef.current = onDraftChange;

	// Once the async form-def loads, backfill dataType for any condition rows
	// that were hydrated without it (custom fields whose dataType was unknown at
	// hydration time, plus a safety net for built-in fields).
	useEffect(() => {
		const conditions = conditionsRef.current;
		const hasUnresolved = conditions.some((c) => !c.dataType && c.fieldName);
		if (!hasUnresolved) return;
		const allFields = [...RULE_FORM_CONDITION_FIELDS, ...customConditionFields];
		onDraftChangeRef.current({
			conditions: conditions.map((c) => {
				if (c.dataType || !c.fieldName) return c;
				const lower = c.fieldName.toLowerCase();
				const found = allFields.find((f) => f.id.toLowerCase() === lower);
				// Also normalise fieldName to the canonical id in case of case mismatch.
				return found
					? { ...c, fieldName: found.id, dataType: found.dataType }
					: c;
			}),
		});
	}, [customConditionFields]);

	const conditionOperatorsQuery = useQuery(
		workflowQueryOptions.conditionOperatorsByDataType,
	);
	const operatorsByDataType = useMemo<Record<string, string[]>>(() => {
		const map: Record<string, string[]> = {};
		for (const item of conditionOperatorsQuery.data ?? []) {
			map[item.dataType] = item.operators;
		}
		return map;
	}, [conditionOperatorsQuery.data]);

	const hasSetAssigneeAction = draft.actions.some(
		(a) => a.type === "set_assignee",
	);
	const orgUsersQuery = useQuery({
		...organizationQueryOptions.users.list(organizationId, 1, 100),
		enabled:
			mode === "organization" && !!organizationId && hasSetAssigneeAction,
	});
	const orgUsers = orgUsersQuery.data ?? [];

	const updateCondition = (id: string, patch: Partial<ConditionRow>) =>
		onDraftChange({
			conditions: draft.conditions.map((c) =>
				c.id === id ? { ...c, ...patch } : c,
			),
		});

	const removeCondition = (id: string) =>
		onDraftChange({
			conditions: draft.conditions.filter((c) => c.id !== id),
		});

	const updateAction = (id: string, patch: Partial<ActionRow>) =>
		onDraftChange({
			actions: draft.actions.map((a) => (a.id === id ? { ...a, ...patch } : a)),
		});

	const removeAction = (id: string) =>
		onDraftChange({
			actions: draft.actions.filter((a) => a.id !== id),
		});

	const handleActionTypeChange = (actionId: string, type: string) => {
		const newType = type as ActionRow["type"];
		updateAction(actionId, {
			type: newType,
			params: makeParamsForActionType(newType),
		});
	};

	const actionTypeOptions = [
		{
			id: "set_status",
			label: t("admin.workflowRules.create.actions.type.setStatus"),
		},
		{
			id: "set_assignee",
			label: t("admin.workflowRules.create.actions.type.setAssignee"),
		},
		{
			id: "send_email",
			label: t("admin.workflowRules.create.actions.type.sendEmail"),
		},
		{
			id: "create_entity",
			label: t("admin.workflowRules.create.actions.type.createEntity"),
		},
	].filter(
		(opt) =>
			!(entityKind === "RecordType" && opt.id === "set_status") &&
			!(entityKind === "AssetType" && opt.id === "set_assignee") &&
			!(entityKind === "RecordType" && opt.id === "set_assignee"),
	);

	const triggerOptions =
		entityKind === "RecordType"
			? [
					{
						id: "entity_created",
						label: t(
							"admin.workflowRules.create.fields.trigger.options.entityCreated",
						),
					},
				]
			: [
					{
						id: "entity_created",
						label: t(
							"admin.workflowRules.create.fields.trigger.options.entityCreated",
						),
					},
					{
						id: "status_entered",
						label: t(
							"admin.workflowRules.create.fields.trigger.options.statusEntered",
						),
					},
					{
						id: "status_exited",
						label: t(
							"admin.workflowRules.create.fields.trigger.options.statusExited",
						),
					},
				];

	return (
		<div className="space-y-5 px-5 pb-5">
			{/* Name */}
			<div>
				<Input
					label={t("admin.workflowRules.create.fields.name.label")}
					placeholder={t("admin.workflowRules.create.fields.name.placeholder")}
					value={draft.name}
					isRequired
					onChange={(v) => {
						onDraftChange({ name: v });
						onErrorsClear(["name"]);
					}}
					isInvalid={Boolean(errors.name)}
				/>
				<InlineError message={errors.name} />
			</div>

			{/* Description */}
			<TextArea
				label={t("admin.workflowRules.create.fields.description.label")}
				placeholder={t(
					"admin.workflowRules.create.fields.description.placeholder",
				)}
				value={draft.description}
				onChange={(v) => onDraftChange({ description: v })}
			/>

			{/* Trigger */}
			<div>
				<Select
					label={t("admin.workflowRules.create.fields.trigger.label")}
					isRequired
					selectedKey={draft.triggerType || null}
					onSelectionChange={(key) => {
						const val = key as TriggerType;
						onDraftChange({
							triggerType: val,
							triggerStatusId:
								val === "entity_created" ? "" : draft.triggerStatusId,
						});
						onErrorsClear(["trigger", "triggerStatus"]);
					}}
					placeholder={t(
						"admin.workflowRules.create.fields.trigger.placeholder",
					)}
					isInvalid={Boolean(errors.trigger)}
				>
					{triggerOptions.map((opt) => (
						<SelectItem key={opt.id} id={opt.id} label={opt.label} />
					))}
				</Select>
				<p className="mt-1 text-xs text-tertiary">
					{t("admin.workflowRules.create.fields.trigger.help")}
				</p>
				<InlineError message={errors.trigger} />
			</div>

			{/* Target Status (conditional) */}
			{(draft.triggerType === "status_entered" ||
				draft.triggerType === "status_exited") && (
				<div>
					<Select
						label={
							draft.triggerType === "status_entered"
								? t("admin.workflowRules.create.fields.targetStatus.entered")
								: t("admin.workflowRules.create.fields.targetStatus.exited")
						}
						isRequired
						selectedKey={draft.triggerStatusId || null}
						onSelectionChange={(key) => {
							onDraftChange({ triggerStatusId: key as string });
							onErrorsClear(["triggerStatus"]);
						}}
						placeholder={t(
							"admin.workflowRules.create.fields.targetStatus.placeholder",
						)}
						isInvalid={Boolean(errors.triggerStatus)}
						isDisabled={lifecycleStatuses.length === 0}
					>
						{lifecycleStatuses.map((s) => (
							<SelectItem key={s.id} id={s.id} label={s.name ?? s.id} />
						))}
					</Select>
					<InlineError message={errors.triggerStatus} />
				</div>
			)}

			{/* Conditions */}
			<div className="rounded-lg border border-secondary p-4">
				<SectionHeader
					title={t("admin.workflowRules.create.conditions.title")}
					description={t("admin.workflowRules.create.conditions.description")}
				/>
				<div className="mt-3 flex flex-col gap-3">
					{draft.conditions.length === 0 ? (
						<p className="text-xs text-tertiary">
							{t("admin.workflowRules.create.conditions.empty")}
						</p>
					) : (
						draft.conditions.map((cond, i) => (
							<div
								key={cond.id}
								className="flex flex-col gap-2 rounded-md border border-secondary p-3"
							>
								<div className="flex items-start gap-2">
									<div className="flex-1">
										<Select
											label={t(
												"admin.workflowRules.create.conditions.field.label",
											)}
											selectedKey={cond.fieldName || null}
											onSelectionChange={(key) => {
												const allFields = [
													...RULE_FORM_CONDITION_FIELDS,
													...customConditionFields,
												];
												const found = allFields.find((f) => f.id === key);
												updateCondition(cond.id, {
													fieldName: key as string,
													fieldLabel: found?.label ?? "",
													dataType: found?.dataType ?? "",
													operator: "",
													value:
														found?.dataType?.toLowerCase() === "boolean"
															? "false"
															: "",
													validValues:
														found && "validValues" in found
															? (found.validValues as string[] | undefined)
															: undefined,
												});
											}}
											placeholder={t(
												"admin.workflowRules.create.conditions.field.placeholder",
											)}
											size="sm"
										>
											{[
												...RULE_FORM_CONDITION_FIELDS.filter(
													(f) =>
														!(
															(entityKind === "AssetType" ||
																entityKind === "RecordType") &&
															f.id === "assignee"
														),
												),
												...customConditionFields,
											].map((f) => (
												<SelectItem key={f.id} id={f.id} label={f.label} />
											))}
										</Select>
									</div>
									<div className="flex-1">
										<Select
											label={t(
												"admin.workflowRules.create.conditions.operator.label",
											)}
											selectedKey={cond.operator || null}
											onSelectionChange={(key) => {
												const op = key as string;
												updateCondition(cond.id, {
													operator: op,
													value: RULE_FORM_NO_VALUE_OPERATORS.has(op)
														? ""
														: cond.value,
												});
											}}
											placeholder={t(
												"admin.workflowRules.create.conditions.operator.placeholder",
											)}
											isDisabled={
												operatorsForDataType(
													cond.dataType,
													operatorsByDataType,
												) === null
											}
											size="sm"
										>
											{(
												operatorsForDataType(
													cond.dataType,
													operatorsByDataType,
												) ?? []
											).map((op) => (
												<SelectItem
													key={op}
													id={op}
													label={formatOperatorLabel(op, t)}
												/>
											))}
										</Select>
									</div>
									<button
										type="button"
										className="mt-5 shrink-0 rounded-md p-1 text-tertiary hover:bg-secondary-subtle hover:text-primary"
										aria-label={t(
											"admin.workflowRules.create.conditions.remove",
										)}
										onClick={() => removeCondition(cond.id)}
									>
										<Trash02 size={16} />
									</button>
								</div>

								{!RULE_FORM_NO_VALUE_OPERATORS.has(cond.operator) &&
									(() => {
										const dt = cond.dataType.toLowerCase();
										if (dt === "boolean") {
											return (
												<div className="flex items-center gap-2 py-1">
													<span className="text-sm font-medium text-secondary">
														{t(
															"admin.workflowRules.create.conditions.value.label",
														)}
													</span>
													<Checkbox
														isSelected={cond.value === "true"}
														onChange={(checked) =>
															updateCondition(cond.id, {
																value: checked ? "true" : "false",
															})
														}
														aria-label={t(
															"admin.workflowRules.create.conditions.value.label",
														)}
													/>
												</div>
											);
										}
										if (cond.validValues && cond.validValues.length > 0) {
											return (
												<Select
													label={t(
														"admin.workflowRules.create.conditions.value.label",
													)}
													selectedKey={cond.value || null}
													onSelectionChange={(key) =>
														updateCondition(cond.id, { value: key as string })
													}
													placeholder={t(
														"admin.workflowRules.create.conditions.value.placeholder",
													)}
													size="sm"
												>
													{cond.validValues.map((v) => (
														<SelectItem key={v} id={v} label={v} />
													))}
												</Select>
											);
										}
										if (dt === "date" || dt === "datetime") {
											return (
												<ConditionDateValueInput
													label={t(
														"admin.workflowRules.create.conditions.value.label",
													)}
													value={cond.value}
													onChange={(v) =>
														updateCondition(cond.id, { value: v })
													}
													showTime={dt === "datetime"}
												/>
											);
										}
										return (
											<Input
												label={t(
													"admin.workflowRules.create.conditions.value.label",
												)}
												placeholder={t(
													"admin.workflowRules.create.conditions.value.placeholder",
												)}
												value={cond.value}
												onChange={(v) => updateCondition(cond.id, { value: v })}
												inputMode={
													dt === "number" ||
													dt === "integer" ||
													dt === "decimal"
														? "numeric"
														: undefined
												}
												size="sm"
											/>
										);
									})()}

								<InlineError message={errors[`condition_${i}`]} />
							</div>
						))
					)}

					{draft.conditions.length < 5 && (
						<Button
							type="button"
							color="secondary"
							size="sm"
							iconLeading={Plus}
							onClick={() =>
								onDraftChange({
									conditions: [...draft.conditions, makeEmptyConditionRow()],
								})
							}
						>
							{t("admin.workflowRules.create.conditions.add")}
						</Button>
					)}

					{draft.conditions.length > 0 && (
						<p className="text-xs text-tertiary">
							{t("admin.workflowRules.create.conditions.allMustBeTrue")}
						</p>
					)}
				</div>
			</div>

			{/* Actions */}
			<div className="rounded-lg border border-secondary p-4">
				<SectionHeader
					title={t("admin.workflowRules.create.actions.title")}
					description={t("admin.workflowRules.create.actions.description")}
				/>
				<InlineError message={errors.actions} />
				<div className="mt-3 flex flex-col gap-3">
					{draft.actions.map((action, i) => (
						<DrawerActionRowBuilder
							key={action.id}
							action={action}
							index={i}
							canRemove={draft.actions.length > 1}
							mode={mode}
							actionTypeOptions={actionTypeOptions}
							lifecycleStatuses={lifecycleStatuses}
							recordTypes={recordTypesQuery.data ?? []}
							jobTypes={jobTypesQuery.data ?? []}
							ticketTypes={ticketTypesQuery.data ?? []}
							ticketPriorities={ticketPriorities}
							orgUsers={orgUsers}
							errors={errors}
							onTypeChange={(type) => handleActionTypeChange(action.id, type)}
							onParamsChange={(params) => updateAction(action.id, { params })}
							onRemove={() => removeAction(action.id)}
							t={t}
						/>
					))}

					{draft.actions.length < 5 && (
						<Button
							type="button"
							color="secondary"
							size="sm"
							iconLeading={Plus}
							onClick={() =>
								onDraftChange({
									actions: [...draft.actions, makeEmptyActionRow()],
								})
							}
						>
							{t("admin.workflowRules.create.actions.add")}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}

// ─── DrawerActionRowBuilder ────────────────────────────────────────────────────

interface DrawerActionRowBuilderProps {
	action: ActionRow;
	index: number;
	canRemove: boolean;
	mode: "platform" | "organization";
	actionTypeOptions: { id: string; label: string }[];
	lifecycleStatuses: {
		id: string;
		name?: string | null;
		displayOrder: number;
	}[];
	recordTypes: { id: string; name?: string | null }[];
	jobTypes: { id: string; name?: string | null }[];
	ticketTypes: { id: string; name?: string | null }[];
	ticketPriorities: { id: string; name: string }[];
	orgUsers: {
		userId: string;
		firstName: string;
		lastName: string;
		displayName: string | null;
		email: string;
	}[];
	errors: Record<string, string>;
	onTypeChange: (type: string) => void;
	onParamsChange: (params: ActionParams) => void;
	onRemove: () => void;
	t: ReturnType<typeof useAppTranslation>["t"];
}

function DrawerActionRowBuilder({
	action,
	index,
	canRemove,
	mode,
	actionTypeOptions,
	lifecycleStatuses,
	recordTypes,
	jobTypes,
	ticketTypes,
	ticketPriorities,
	orgUsers,
	errors,
	onTypeChange,
	onParamsChange,
	onRemove,
	t,
}: DrawerActionRowBuilderProps) {
	const params = action.params;

	const entityTypeItems =
		action.type === "create_entity"
			? (() => {
					const p = params as Extract<ActionParams, { type: "create_entity" }>;
					if (p.entityClass === "record")
						return recordTypes.map((r) => ({
							id: r.id,
							name: r.name ?? r.id,
						}));
					if (p.entityClass === "job")
						return jobTypes.map((j) => ({ id: j.id, name: j.name ?? j.id }));
					if (p.entityClass === "ticket")
						return ticketTypes.map((tk) => ({
							id: tk.id,
							name: tk.name ?? tk.id,
						}));
					return [];
				})()
			: [];

	return (
		<div className="flex flex-col gap-3 rounded-md border border-secondary p-3">
			<div className="flex items-start gap-2">
				<div className="flex-1">
					<Select
						label={t("admin.workflowRules.create.actions.type.label")}
						isRequired
						selectedKey={action.type || null}
						onSelectionChange={(key) => onTypeChange(key as string)}
						placeholder={t(
							"admin.workflowRules.create.actions.type.placeholder",
						)}
						isInvalid={Boolean(errors[`action_${index}_type`])}
						size="sm"
					>
						{actionTypeOptions.map((opt) => (
							<SelectItem key={opt.id} id={opt.id} label={opt.label} />
						))}
					</Select>
					<InlineError message={errors[`action_${index}_type`]} />
				</div>

				<button
					type="button"
					className="mt-5 shrink-0 rounded-md p-1 text-tertiary hover:bg-secondary-subtle hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
					aria-label={t("admin.workflowRules.create.actions.remove")}
					disabled={!canRemove}
					onClick={onRemove}
				>
					<Trash02 size={16} />
				</button>
			</div>

			{/* set_status */}
			{action.type === "set_status" && (
				<div>
					<Select
						label={t("admin.workflowRules.create.actions.setStatus.label")}
						isRequired
						selectedKey={
							(params as Extract<ActionParams, { type: "set_status" }>)
								.statusId || null
						}
						onSelectionChange={(key) =>
							onParamsChange({ type: "set_status", statusId: key as string })
						}
						placeholder={t(
							"admin.workflowRules.create.actions.setStatus.placeholder",
						)}
						isInvalid={Boolean(errors[`action_${index}_statusId`])}
						isDisabled={lifecycleStatuses.length === 0}
						size="sm"
					>
						{lifecycleStatuses.map((s) => (
							<SelectItem key={s.id} id={s.id} label={s.name ?? s.id} />
						))}
					</Select>
					<InlineError message={errors[`action_${index}_statusId`]} />
				</div>
			)}

			{/* set_assignee */}
			{action.type === "set_assignee" && (
				<>
					{mode === "organization" ? (
						<Select
							label={t("admin.workflowRules.create.actions.setAssignee.label")}
							isRequired
							selectedKey={
								(params as Extract<ActionParams, { type: "set_assignee" }>)
									.assignee || null
							}
							onSelectionChange={(key) => {
								const userId = key as string;
								const user = orgUsers.find((u) => u.userId === userId);
								const assigneeName = user
									? (user.displayName ??
										`${user.firstName} ${user.lastName}`.trim())
									: undefined;
								onParamsChange({
									type: "set_assignee",
									assignee: userId,
									assigneeName,
								});
							}}
							placeholder={t(
								"admin.workflowRules.create.actions.setAssignee.placeholder",
							)}
							isInvalid={Boolean(errors[`action_${index}_assignee`])}
							isDisabled={orgUsers.length === 0}
							size="sm"
						>
							{orgUsers.map((u) => (
								<SelectItem
									key={u.userId}
									id={u.userId}
									label={u.displayName ?? `${u.firstName} ${u.lastName}`}
									supportingText={u.email}
								/>
							))}
						</Select>
					) : (
						<Input
							label={t("admin.workflowRules.create.actions.setAssignee.label")}
							isRequired
							placeholder={t(
								"admin.workflowRules.create.actions.setAssignee.placeholder",
							)}
							value={
								(params as Extract<ActionParams, { type: "set_assignee" }>)
									.assignee
							}
							onChange={(v) =>
								onParamsChange({ type: "set_assignee", assignee: v })
							}
							isInvalid={Boolean(errors[`action_${index}_assignee`])}
							size="sm"
						/>
					)}
					<InlineError message={errors[`action_${index}_assignee`]} />
				</>
			)}

			{/* send_email */}
			{action.type === "send_email" &&
				(() => {
					const p = params as Extract<ActionParams, { type: "send_email" }>;
					return (
						<>
							<div>
								<Select
									label={t(
										"admin.workflowRules.create.actions.sendEmail.recipientType.label",
									)}
									isRequired
									selectedKey={p.recipientType || null}
									onSelectionChange={(key) =>
										onParamsChange({
											...p,
											recipientType: key as "Assignee" | "Owner" | "Custom",
											recipientEmail: key !== "Custom" ? "" : p.recipientEmail,
										})
									}
									placeholder={t(
										"admin.workflowRules.create.actions.sendEmail.recipientType.placeholder",
									)}
									isInvalid={Boolean(errors[`action_${index}_recipientType`])}
									size="sm"
								>
									<SelectItem
										key="Assignee"
										id="Assignee"
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientType.assignee",
										)}
									/>
									<SelectItem
										key="Owner"
										id="Owner"
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientType.owner",
										)}
									/>
									<SelectItem
										key="Custom"
										id="Custom"
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientType.custom",
										)}
									/>
								</Select>
								<InlineError
									message={errors[`action_${index}_recipientType`]}
								/>
							</div>

							{p.recipientType === "Custom" && (
								<div>
									<Input
										label={t(
											"admin.workflowRules.create.actions.sendEmail.recipientEmail.label",
										)}
										isRequired
										placeholder="email@example.com"
										value={p.recipientEmail}
										type="email"
										onChange={(v) =>
											onParamsChange({ ...p, recipientEmail: v })
										}
										isInvalid={Boolean(
											errors[`action_${index}_recipientEmail`],
										)}
										size="sm"
									/>
									<InlineError
										message={errors[`action_${index}_recipientEmail`]}
									/>
									<p className="mt-1 text-xs text-tertiary">
										{t(
											"admin.workflowRules.create.actions.sendEmail.recipientEmail.help",
										)}
									</p>
								</div>
							)}
						</>
					);
				})()}

			{/* create_entity */}
			{action.type === "create_entity" &&
				(() => {
					const p = params as Extract<ActionParams, { type: "create_entity" }>;
					return (
						<>
							<div>
								<Select
									label={t(
										"admin.workflowRules.create.actions.createEntity.entityClass.label",
									)}
									isRequired
									selectedKey={p.entityClass || null}
									onSelectionChange={(key) =>
										onParamsChange({
											...p,
											entityClass: key as "asset" | "record" | "job" | "ticket",
											entityTypeId: "",
										})
									}
									placeholder={t(
										"admin.workflowRules.create.actions.createEntity.entityClass.placeholder",
									)}
									isInvalid={Boolean(errors[`action_${index}_entityClass`])}
									size="sm"
								>
									<SelectItem
										key="asset"
										id="asset"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.asset",
										)}
									/>
									<SelectItem
										key="record"
										id="record"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.record",
										)}
									/>
									<SelectItem
										key="job"
										id="job"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.job",
										)}
									/>
									<SelectItem
										key="ticket"
										id="ticket"
										label={t(
											"admin.workflowRules.create.actions.createEntity.entityClass.ticket",
										)}
									/>
								</Select>
								<InlineError message={errors[`action_${index}_entityClass`]} />
							</div>

							{p.entityClass && (
								<div>
									{p.entityClass === "asset" ? (
										<>
											<p className="mb-1 text-xs font-medium text-secondary">
												{t(
													"admin.workflowRules.create.actions.createEntity.entityType.label",
												)}
											</p>
											<div className="rounded-lg border border-secondary bg-secondary px-3 py-2 text-xs text-disabled">
												{t(
													"admin.workflowRules.create.actions.createEntity.entityType.assetUnavailable",
												)}
											</div>
										</>
									) : (
										<>
											<Select
												label={t(
													"admin.workflowRules.create.actions.createEntity.entityType.label",
												)}
												isRequired
												selectedKey={p.entityTypeId || null}
												onSelectionChange={(key) =>
													onParamsChange({
														...p,
														entityTypeId: key as string,
													})
												}
												placeholder={t(
													"admin.workflowRules.create.actions.createEntity.entityType.placeholder",
												)}
												isInvalid={Boolean(
													errors[`action_${index}_entityTypeId`],
												)}
												size="sm"
											>
												{entityTypeItems.map((et) => (
													<SelectItem key={et.id} id={et.id} label={et.name} />
												))}
											</Select>
											<InlineError
												message={errors[`action_${index}_entityTypeId`]}
											/>
										</>
									)}
								</div>
							)}

							<Input
								label={t(
									"admin.workflowRules.create.actions.createEntity.name.label",
								)}
								isRequired
								placeholder={t(
									"admin.workflowRules.create.actions.createEntity.name.placeholder",
								)}
								value={p.entityName}
								maxLength={255}
								onChange={(v) => onParamsChange({ ...p, entityName: v })}
								isInvalid={Boolean(errors[`action_${index}_entityName`])}
								size="sm"
							/>
							<InlineError message={errors[`action_${index}_entityName`]} />

							{p.entityClass === "ticket" && (
								<>
									<Select
										label={t(
											"admin.workflowRules.create.actions.createEntity.priorityId.label",
										)}
										isRequired
										selectedKey={p.priorityId || null}
										onSelectionChange={(key) =>
											onParamsChange({ ...p, priorityId: key as string })
										}
										placeholder={t(
											"admin.workflowRules.create.actions.createEntity.priorityId.placeholder",
										)}
										isInvalid={Boolean(errors[`action_${index}_priorityId`])}
										isDisabled={ticketPriorities.length === 0}
										size="sm"
									>
										{ticketPriorities.map((pr) => (
											<SelectItem key={pr.id} id={pr.id} label={pr.name} />
										))}
									</Select>
									<InlineError message={errors[`action_${index}_priorityId`]} />
								</>
							)}
						</>
					);
				})()}
		</div>
	);
}

// ─── Activity tab ─────────────────────────────────────────────────────────────

function ActivityTab({ rule }: { rule: WorkflowRuleDisplay }) {
	const { t } = useAppTranslation();
	const { createdAt, updatedAt, createdByUser, updatedByUser } =
		getEntityAuditDisplayFields(rule);

	return (
		<div className="space-y-6 px-5 pb-5">
			<div>
				<h3 className="text-[20px] font-semibold text-gray-900">
					{t("admin.workflowRules.drawer.activity.title")}
				</h3>
				<p className="text-sm text-gray-500">
					{t("admin.workflowRules.drawer.activity.description")}
				</p>
			</div>

			<div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-6">
				<ActivityField
					label={t("admin.workflowRules.drawer.activity.metadata.created")}
					icon={<Calendar className="size-4 text-gray-400" />}
					value={createdAt ?? <EmptyValue />}
				/>
				<ActivityField
					label={t("admin.workflowRules.drawer.activity.metadata.createdBy")}
					icon={<User01 className="size-4 text-gray-400" />}
					value={createdByUser ?? <EmptyValue />}
				/>
				<ActivityField
					label={t("admin.workflowRules.drawer.activity.metadata.lastUpdate")}
					icon={<Calendar className="size-4 text-gray-400" />}
					value={updatedAt ?? <EmptyValue />}
				/>
				<ActivityField
					label={t("admin.workflowRules.drawer.activity.metadata.lastUpdateBy")}
					icon={<User01 className="size-4 text-gray-400" />}
					value={updatedByUser ?? <EmptyValue />}
				/>
			</div>

			<div className="space-y-3 rounded-lg border border-gray-200 bg-white p-6">
				<div>
					<h4 className="text-[20px] font-semibold text-gray-900">
						{t("admin.workflowRules.drawer.activity.history.title")}
					</h4>
					<p className="text-sm text-gray-500">
						{t("admin.workflowRules.drawer.activity.history.description")}
					</p>
				</div>
				<p className="text-sm text-gray-400">
					{t("admin.workflowRules.drawer.activity.placeholder")}
				</p>
			</div>
		</div>
	);
}

function ActivityField({
	label,
	icon,
	value,
}: {
	label: string;
	icon: ReactNode;
	value: ReactNode;
}) {
	return (
		<div>
			<p className="mb-1 text-sm uppercase tracking-wide text-gray-500">
				{label}
			</p>
			<div className="flex items-center gap-2">
				{icon}
				<span className="text-sm font-semibold text-gray-900">{value}</span>
			</div>
		</div>
	);
}

// ─── Main component ───────────────────────────────────────────────────────────

type ConfirmAction = "activate" | "archive" | null;

export function WorkflowRuleDetailDrawer({
	ruleId,
	organizationId,
	lifecycleId,
	mode = "platform",
	canEdit = true,
	entityTypeId,
	onClose,
	onFullView,
}: WorkflowRuleDetailDrawerProps) {
	const { t } = useAppTranslation();
	const toastId = useId();

	const [isOpen, setIsOpen] = useState(!!ruleId);
	useEffect(() => {
		setIsOpen(!!ruleId);
	}, [ruleId]);

	const [activeTab, setActiveTab] = useState<DrawerTab>("details");
	const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editDraft, setEditDraft] = useState<RuleFormDraft>(
		makeEmptyRuleFormDraft,
	);
	const [editErrors, setEditErrors] = useState<Record<string, string>>({});

	// ── Data ──
	const detailQuery = useQuery({
		...workflowQueryOptions.rules.detail(ruleId ?? "", { organizationId }),
		enabled: !!ruleId,
	});

	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteWorkflowRule",
			entityType: "WorkflowRule",
			entityId: ruleId ?? "",
		}),
		enabled: !!ruleId,
	});
	const canDelete = Boolean(canDeleteQuery.data);

	const canArchiveQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "ArchiveWorkflowRule",
			entityType: "WorkflowRule",
			entityId: ruleId ?? "",
		}),
		enabled: !!ruleId,
	});
	const canArchive = Boolean(canArchiveQuery.data);

	const activateVersionId =
		detailQuery.data?.latestVersion?.id ??
		detailQuery.data?.activeVersion?.id ??
		"";

	const canActivateQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "ActivateWorkflowRuleVersion",
			entityType: "WorkflowRuleVersion",
			entityId: activateVersionId,
		}),
		enabled: !!activateVersionId && detailQuery.data?.status === "draft",
	});
	const canActivate = Boolean(canActivateQuery.data);

	const lifecycleDetailQuery = useQuery({
		...lifecycleQueryOptions.statusLifecycleDetail(
			lifecycleId ?? "",
			organizationId,
		),
		enabled: !!lifecycleId,
	});

	const statusNameMap = useMemo<Record<string, string>>(() => {
		const statuses = lifecycleDetailQuery.data?.statuses ?? [];
		const map: Record<string, string> = {};
		for (const s of statuses) {
			if (s.id && s.name) map[s.id.toLowerCase()] = s.name;
		}
		return map;
	}, [lifecycleDetailQuery.data]);

	const lifecycleStatuses = lifecycleDetailQuery.data?.statuses ?? [];

	const viewOrgUsersQuery = useQuery({
		...organizationQueryOptions.users.list(organizationId, 1, 100),
		enabled: mode === "organization" && !!organizationId,
	});
	const userNameMap = useMemo<Record<string, string>>(() => {
		const map: Record<string, string> = {};
		for (const u of viewOrgUsersQuery.data ?? []) {
			if (u.userId) {
				const name =
					u.displayName ??
					([u.firstName, u.lastName].filter(Boolean).join(" ") || u.email);
				map[u.userId] = name;
			}
		}
		return map;
	}, [viewOrgUsersQuery.data]);

	// Derive entity kind from the fetched rule so we can query the right type's sub-orgs.
	const viewEntityKind =
		ENTITY_TYPE_TO_KIND[detailQuery.data?.entityType ?? ""] ?? "AssetType";
	const viewEntityTypeId = entityTypeId ?? "";

	// View-mode sub-org name map: fetch only sub-orgs assigned to this entity type.
	const viewJobTypeSubOrgsQuery = useQuery({
		...jobQueryOptions.jobTypeDetail(viewEntityTypeId, { organizationId }),
		enabled:
			mode === "organization" &&
			viewEntityKind === "JobType" &&
			!!viewEntityTypeId &&
			!!detailQuery.data?.entityType,
	});
	const viewTicketTypeSubOrgsQuery = useQuery({
		...ticketQueryOptions.ticketTypeDetail(viewEntityTypeId, {
			organizationId,
		}),
		enabled:
			mode === "organization" &&
			viewEntityKind === "TicketType" &&
			!!viewEntityTypeId &&
			!!detailQuery.data?.entityType,
	});
	const viewAssetTypeSubOrgsQuery = useQuery({
		...assetQueryOptions.typeDetail(viewEntityTypeId, { organizationId }),
		enabled:
			mode === "organization" &&
			viewEntityKind === "AssetType" &&
			!!viewEntityTypeId &&
			!!detailQuery.data?.entityType,
	});
	const viewRecordTypeSubOrgsQuery = useQuery({
		...recordQueryOptions.recordTypeDetail(viewEntityTypeId, {
			organizationId,
		}),
		enabled:
			mode === "organization" &&
			viewEntityKind === "RecordType" &&
			!!viewEntityTypeId &&
			!!detailQuery.data?.entityType,
	});
	const viewTypeAssignedSubOrgs = useMemo(() => {
		if (viewEntityKind === "JobType")
			return viewJobTypeSubOrgsQuery.data?.jobTypeSubOrganizations ?? [];
		if (viewEntityKind === "TicketType")
			return viewTicketTypeSubOrgsQuery.data?.ticketTypeSubOrganizations ?? [];
		if (viewEntityKind === "AssetType")
			return viewAssetTypeSubOrgsQuery.data?.assetTypeSubOrganizations ?? [];
		if (viewEntityKind === "RecordType")
			return viewRecordTypeSubOrgsQuery.data?.recordTypeSubOrganizations ?? [];
		return [];
	}, [
		viewEntityKind,
		viewJobTypeSubOrgsQuery.data,
		viewTicketTypeSubOrgsQuery.data,
		viewAssetTypeSubOrgsQuery.data,
		viewRecordTypeSubOrgsQuery.data,
	]);
	const subOrgNameMap = useMemo<Record<string, string>>(() => {
		const map: Record<string, string> = {};
		for (const s of viewTypeAssignedSubOrgs) {
			if (s.id) map[s.id] = s.name ?? s.id;
		}
		return map;
	}, [viewTypeAssignedSubOrgs]);

	// ── Mutations ──
	const archiveMutation = useArchiveWorkflowRule();
	const activateMutation = useActivateWorkflowRuleVersion();
	const editMutation = useEditWorkflowRule();

	const isMutating =
		archiveMutation.isPending ||
		activateMutation.isPending ||
		editMutation.isPending;

	// ── Helpers ──
	const rule = detailQuery.data ?? null;
	const isLoading = !!ruleId && detailQuery.isLoading;
	const isError =
		!!ruleId && !detailQuery.isLoading && (detailQuery.isError || !rule);
	const isReady =
		!!ruleId && !detailQuery.isLoading && !detailQuery.isError && !!rule;
	const isDraft = rule?.status === "draft";
	const isActive = rule?.status === "active";

	const entityKind = ENTITY_TYPE_TO_KIND[rule?.entityType ?? ""] ?? "AssetType";

	const handleClose = () => {
		if (!isMutating) {
			setIsEditing(false);
			setIsOpen(false);
			setConfirmAction(null);
			setTimeout(() => {
				onClose();
			}, 200);
		}
	};

	const handleFullView = () => {
		if (!ruleId || !onFullView) return;
		setIsEditing(false);
		setIsOpen(false);
		setConfirmAction(null);
		onFullView(ruleId);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) handleClose();
	};

	// ── Edit helpers ──
	const enterEditMode = () => {
		if (!rule) return;
		setEditDraft(hydrateRuleFormDraft(rule));
		setEditErrors({});
		setIsEditing(true);
	};

	const cancelEdit = () => {
		setIsEditing(false);
		setEditErrors({});
		editMutation.reset();
	};

	const handleSaveEdit = async () => {
		if (!rule) return;
		const versionId = rule.latestVersion?.id ?? rule.activeVersion?.id ?? "";
		if (!versionId) return;

		const validation = validateRuleFormDraft(editDraft, entityKind);
		if (!validation.ok) {
			setEditErrors(validation.errors);
			return;
		}
		setEditErrors({});

		const versionPayload = buildRuleVersionPayload(editDraft);

		editMutation.mutate(
			{
				ruleId: rule.id,
				versionId,
				organizationId,
				meta: {
					name: editDraft.name.trim(),
					description: editDraft.description.trim() || null,
				},
				version: versionPayload,
			},
			{
				onSuccess: () => {
					toast.custom(
						(tid) => (
							<Toast
								id={toastId}
								color="success"
								addonText="Success"
								message={t("admin.workflowRules.edit.toast.success")}
								showCloseButton
								onClose={() => toast.dismiss(tid)}
							/>
						),
						{ duration: 5000 },
					);
					setIsEditing(false);
					setEditErrors({});
				},
				onError: () => {
					toast.custom(
						(tid) => (
							<Toast
								id={toastId}
								color="error"
								addonText="Error"
								message={t("admin.workflowRules.edit.toast.error")}
								showCloseButton
								onClose={() => toast.dismiss(tid)}
							/>
						),
						{ duration: 5000 },
					);
				},
			},
		);
	};

	// ── Action handlers ──
	const handleActivate = () => {
		if (!rule) return;
		const versionId = rule.latestVersion?.id ?? rule.activeVersion?.id ?? "";
		if (!versionId) return;
		activateMutation.mutate(
			{ ruleId: rule.id, versionId, organizationId },
			{
				onSuccess: () => {
					toast.custom(
						(tid) => (
							<Toast
								id={toastId}
								color="success"
								addonText="Success"
								message={t("admin.workflowRules.drawer.toasts.activated")}
								showCloseButton
								onClose={() => toast.dismiss(tid)}
							/>
						),
						{ duration: 5000 },
					);
					setConfirmAction(null);
					handleClose();
				},
				onError: () => {
					toast.custom(
						(tid) => (
							<Toast
								id={toastId}
								color="error"
								addonText="Error"
								message={t("admin.workflowRules.drawer.toasts.activateFailed")}
								showCloseButton
								onClose={() => toast.dismiss(tid)}
							/>
						),
						{ duration: 5000 },
					);
					setConfirmAction(null);
				},
			},
		);
	};

	const handleArchive = () => {
		if (!rule) return;
		archiveMutation.mutate(
			{ ruleId: rule.id, organizationId },
			{
				onSuccess: () => {
					toast.custom(
						(tid) => (
							<Toast
								id={toastId}
								color="success"
								addonText="Success"
								message={t("admin.workflowRules.drawer.toasts.archived")}
								showCloseButton
								onClose={() => toast.dismiss(tid)}
							/>
						),
						{ duration: 5000 },
					);
					setConfirmAction(null);
					handleClose();
				},
				onError: () => {
					toast.custom(
						(tid) => (
							<Toast
								id={toastId}
								color="error"
								addonText="Error"
								message={t("admin.workflowRules.drawer.toasts.archiveFailed")}
								showCloseButton
								onClose={() => toast.dismiss(tid)}
							/>
						),
						{ duration: 5000 },
					);
					setConfirmAction(null);
				},
			},
		);
	};

	// ── Tabs ──
	const tabs: { id: DrawerTab; label: string }[] = [
		{ id: "details", label: t("admin.workflowRules.drawer.tabs.details") },
		{ id: "activity", label: t("admin.workflowRules.drawer.tabs.activity") },
	];

	return (
		<>
			<Drawer
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				width="40%"
				backdropPreset="transparent"
			>
				{({ close }) => (
					<>
						<Drawer.Header
							showDivider={false}
							title={
								isReady && rule ? (
									<span className="flex flex-wrap items-center gap-2">
										<span>
											{rule.name ?? t("admin.workflowRules.drawer.subtitle")}
										</span>
										<StatusBadge status={rule.status} t={t} />
									</span>
								) : (
									t("admin.workflowRules.drawer.subtitle")
								)
							}
							description={t("admin.workflowRules.drawer.subtitle")}
							showCloseButton
							closeButtonClassName="absolute top-2 right-2 z-10"
							onClose={() => {
								close();
								handleClose();
							}}
						/>

						<Drawer.Content>
							{isLoading ? (
								<div className="flex h-full items-center justify-center">
									<LoadingIndicator
										type="line-simple"
										size="xl"
										supportingText
									/>
								</div>
							) : null}

							{isError ? (
								<div className="flex h-full flex-col items-center justify-center gap-3 text-center">
									<p className="text-sm font-semibold text-error">
										{t("admin.workflowRules.drawer.errors.loadFailed")}
									</p>
								</div>
							) : null}

							{isReady && rule ? (
								<div className="space-y-4">
									<div className="px-5">
										<Tabs
											selectedKey={activeTab}
											onSelectionChange={(key) => {
												if (!isEditing) {
													setActiveTab(key as DrawerTab);
												}
											}}
											className="w-full"
										>
											<Tabs.List type="underline" items={tabs} fullWidth>
												{(tab) => <Tabs.Item {...tab} />}
											</Tabs.List>
										</Tabs>
									</div>

									<TabContentWrapper tabKey={activeTab}>
										{activeTab === "details" ? (
											isEditing ? (
												<EditDetailsTab
													draft={editDraft}
													errors={editErrors}
													entityKind={entityKind}
													entityTypeId={entityTypeId}
													mode={mode}
													organizationId={organizationId}
													lifecycleStatuses={lifecycleStatuses}
													onDraftChange={(patch) =>
														setEditDraft((prev) => ({ ...prev, ...patch }))
													}
													onErrorsClear={(keys) =>
														setEditErrors((prev) => {
															const next = { ...prev };
															for (const k of keys) delete next[k];
															return next;
														})
													}
													t={t}
												/>
											) : (
												<DetailsTab
													rule={rule}
													statusNameMap={statusNameMap}
													userNameMap={userNameMap}
													subOrgNameMap={subOrgNameMap}
													entityKind={entityKind}
													entityTypeId={entityTypeId}
													organizationId={organizationId}
													canEdit={canEdit && isDraft}
													onEditClick={enterEditMode}
													t={t}
												/>
											)
										) : (
											<ActivityTab rule={rule} />
										)}
									</TabContentWrapper>
								</div>
							) : null}
						</Drawer.Content>

						<Drawer.Footer>
							{isLoading || isError ? (
								<div className="flex w-full justify-end">
									<Button
										color="secondary"
										size="md"
										onClick={handleClose}
										className="hover:bg-(--color-hover) hover:text-current"
									>
										{t("common.actions.close")}
									</Button>
								</div>
							) : isReady && rule ? (
								isEditing ? (
									<div className="flex w-full items-center justify-end gap-3">
										<Button
											color="secondary"
											size="md"
											onClick={cancelEdit}
											isDisabled={editMutation.isPending}
										>
											{t("common.actions.cancel")}
										</Button>
										<Button
											color="primary"
											size="md"
											isLoading={editMutation.isPending}
											showTextWhileLoading
											onClick={() => void handleSaveEdit()}
										>
											{editMutation.isPending
												? t("common.actions.saving")
												: t("common.actions.save")}
										</Button>
									</div>
								) : (
									<div className="flex w-full items-center justify-between">
										<div className="flex items-center gap-3">
											{isDraft && canActivate ? (
												<Button
													color="secondary"
													size="md"
													iconLeading={CheckCircle}
													onClick={() => setConfirmAction("activate")}
													isDisabled={isMutating}
												>
													{t("admin.workflowRules.drawer.footer.activate")}
												</Button>
											) : null}
											{isDraft && canDelete ? (
												<Button
													color="secondary-destructive"
													size="md"
													iconLeading={Trash02}
													onClick={() => setIsDeleteModalOpen(true)}
													isDisabled={isMutating}
												>
													{t("admin.workflowRules.drawer.footer.delete")}
												</Button>
											) : null}
										</div>
										<div className="flex items-center gap-3">
											{isActive && canArchive ? (
												<Button
													color="secondary"
													size="md"
													iconLeading={Archive}
													onClick={() => setConfirmAction("archive")}
													isDisabled={isMutating}
												>
													{t("admin.workflowRules.drawer.footer.archive")}
												</Button>
											) : null}
											<Button
												color="secondary"
												size="md"
												onClick={handleClose}
												className="hover:bg-(--color-hover) hover:text-current"
											>
												{t("common.actions.close")}
											</Button>
										</div>
									</div>
								)
							) : null}
						</Drawer.Footer>

						{/* Full View button */}
						{isReady && onFullView ? (
							<Button
								size="sm"
								color="secondary"
								iconLeading={Expand03}
								onClick={handleFullView}
								className="absolute top-3 right-14 z-10"
							>
								{t("admin.workflowRules.drawer.fullViewButton")}
							</Button>
						) : null}
					</>
				)}
			</Drawer>

			{/* Confirm dialogs */}
			{confirmAction === "activate" ? (
				<ConfirmDialog
					title={t("admin.workflowRules.drawer.confirm.activate.title")}
					description={t(
						"admin.workflowRules.drawer.confirm.activate.description",
					)}
					confirmLabel={t("admin.workflowRules.drawer.confirm.activate.action")}
					confirmColor="primary"
					isPending={activateMutation.isPending}
					onConfirm={handleActivate}
					onCancel={() => setConfirmAction(null)}
				/>
			) : null}

			{confirmAction === "archive" ? (
				<ConfirmDialog
					title={t("admin.workflowRules.drawer.confirm.archive.title")}
					description={t(
						"admin.workflowRules.drawer.confirm.archive.description",
					)}
					confirmLabel={t("admin.workflowRules.drawer.confirm.archive.action")}
					confirmColor="primary"
					isPending={archiveMutation.isPending}
					onConfirm={handleArchive}
					onCancel={() => setConfirmAction(null)}
				/>
			) : null}

			<DeleteWorkflowRuleModal
				isOpen={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				handleClose={() => setIsDeleteModalOpen(false)}
				onDeleteSuccess={() => {
					setIsDeleteModalOpen(false);
					handleClose();
				}}
				ruleId={rule?.id ?? ""}
				ruleName={rule?.name ?? ""}
				ruleStatus={rule?.status ?? ""}
				organizationId={organizationId}
			/>
		</>
	);
}
